import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { queueAPI, statusAPI } from '../api/client'
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Clock3,
  Download,
  Globe,
  HardDrive,
  Image,
  Loader,
  Radar,
  RefreshCw,
  ServerCog,
  Upload,
  Wrench,
  FileText,
  Sparkles,
  Link2,
} from 'lucide-react'

const SERVICE_META = {
  parse_archive: { label: 'Парсинг архива', icon: Globe, colorClass: 'text-cyan-300' },
  parse_details: { label: 'Парсинг деталей', icon: Globe, colorClass: 'text-sky-300' },
  parse_author: { label: 'Парсинг автора', icon: Globe, colorClass: 'text-indigo-300' },
  refresh_stats: { label: 'Обновление статистики', icon: Activity, colorClass: 'text-violet-300' },
  download: { label: 'Скачивание', icon: Download, colorClass: 'text-yellow-300' },
  publish_agr: { label: 'Публикация в AGR', icon: Upload, colorClass: 'text-green-300' },
  publish_site: { label: 'Публикация на сайт', icon: Upload, colorClass: 'text-emerald-300' },
  publish_multi: { label: 'Мультипубликация', icon: Upload, colorClass: 'text-lime-300' },
  maintenance: { label: 'Обслуживание', icon: HardDrive, colorClass: 'text-orange-300' },
  storage: { label: 'Хранилище', icon: HardDrive, colorClass: 'text-amber-300' },
  other: { label: 'Другое', icon: AlertCircle, colorClass: 'text-white' },
}

function formatDateTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('ru-RU')
}

function formatSeconds(seconds) {
  const safe = Math.max(0, Math.floor(seconds || 0))
  const hours = Math.floor(safe / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  const secs = safe % 60

  if (hours > 0) return `${hours}ч ${minutes}м ${secs}с`
  if (minutes > 0) return `${minutes}м ${secs}с`
  return `${secs}с`
}

function freshnessState(nextParse) {
  if (!nextParse) return { label: 'Нет расписания', className: 'text-white/50 bg-white/5' }

  const next = new Date(nextParse)
  const diffMs = next.getTime() - Date.now()

  if (diffMs <= 0) {
    return { label: 'Просрочен', className: 'text-red-300 bg-red-500/15' }
  }
  if (diffMs <= 10 * 60 * 1000) {
    return { label: 'Скоро запуск', className: 'text-yellow-300 bg-yellow-500/15' }
  }
  return { label: 'Норма', className: 'text-green-300 bg-green-500/15' }
}

function summarizeQueue(pending = {}) {
  let parse = 0
  let parseDetails = 0
  let download = 0
  let publish = 0
  let backfill = 0
  let other = 0

  Object.entries(pending).forEach(([taskType, count]) => {
    const key = String(taskType || '').toLowerCase()
    const value = Number(count) || 0

    if (key.includes('backfill')) {
      backfill += value
    } else if (key.includes('publish')) {
      publish += value
    } else if (key.includes('download')) {
      download += value
    } else if (key.includes('detail')) {
      parseDetails += value
    } else if (key.includes('parse') || key.includes('scrape')) {
      parse += value
    } else {
      other += value
    }
  })

  return { parse, parseDetails, download, publish, backfill, other }
}

function SummaryCard({ icon: Icon, label, value, subtitle, colorClass = 'text-white' }) {
  return (
    <div className="glass p-4">
      <div className="flex items-center gap-2 mb-2 opacity-60">
        <Icon size={16} />
        <span className="text-sm">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${colorClass}`}>{value}</div>
      {subtitle ? <div className="text-xs opacity-50 mt-1">{subtitle}</div> : null}
    </div>
  )
}

function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 rounded-lg bg-white/10">
        <Icon size={18} />
      </div>
      <div>
        <h2 className="text-lg font-semibold">{title}</h2>
        {subtitle ? <p className="text-sm opacity-50">{subtitle}</p> : null}
      </div>
    </div>
  )
}

function SchedulerState({ scheduler }) {
  const isRunning = Boolean(scheduler?.running)
  return (
    <div className="rounded-xl bg-white/5 p-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-sm opacity-60">Планировщик</div>
        <span
          className={`px-2 py-1 rounded text-xs ${
            isRunning ? 'text-green-300 bg-green-500/15' : 'text-red-300 bg-red-500/15'
          }`}
        >
          {isRunning ? 'работает' : 'остановлен'}
        </span>
      </div>
      <div className="text-sm opacity-70">
        Интервал: {scheduler?.interval_seconds ? `${scheduler.interval_seconds}с` : '—'}
      </div>
      <div className="text-sm opacity-70 mt-1">
        Последний heartbeat: {formatDateTime(scheduler?.last_seen_at)}
      </div>
      <div className="text-xs opacity-50 mt-2">
        Источник: {scheduler?.source || '—'}
      </div>
    </div>
  )
}

function formatRelativeStatus(status) {
  if (!status) return '—'
  const date = new Date(status)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('ru-RU')
}

const WEBMASTER_MODE_META = {
  off: {
    label: 'Выключен',
    description: 'SEO-волны остановлены',
    activeClassName: 'bg-red-500 text-white',
  },
  base: {
    label: 'Обычный',
    description: 'Стандартная частота',
    activeClassName: 'bg-white text-black',
  },
  night: {
    label: 'Ночной',
    description: 'Ускорение только ночью',
    activeClassName: 'bg-indigo-500 text-white',
  },
  fast: {
    label: 'Ускоренный',
    description: 'Максимальная частота всегда',
    activeClassName: 'bg-cyan-500 text-black',
  },
}

const RELATED_WAVE_MODE_META = {
  on: {
    label: 'Включен',
    description: 'Автоволна работает по расписанию',
    activeClassName: 'bg-emerald-500 text-white',
  },
  off: {
    label: 'Выключен',
    description: 'Автоволна остановлена',
    activeClassName: 'bg-red-500 text-white',
  },
}

export default function SystemMonitor() {
  const queryClient = useQueryClient()
  const [rebuildResult, setRebuildResult] = useState(null)
  const [relatedRebuildResult, setRelatedRebuildResult] = useState(null)
  const { data, isLoading, isFetching } = useQuery(
    'systemStatus',
    statusAPI.getSystem,
    { refetchInterval: 5000 }
  )
  const recoverStaleMutation = useMutation(
    () => queueAPI.recoverStale({ older_than_minutes: 30, limit: 100 }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('systemStatus')
      },
    }
  )
  const rebuildHomeFeedMutation = useMutation(
    () => statusAPI.rebuildLojsHomeFeed(),
    {
      onSuccess: (result) => {
        setRebuildResult({
          ok: true,
          at: new Date().toISOString(),
          payload: result,
        })
        queryClient.invalidateQueries('systemStatus')
      },
      onError: (error) => {
        setRebuildResult({
          ok: false,
          at: new Date().toISOString(),
          message: error?.response?.data?.detail || error?.message || 'Не удалось перестроить ленту главной',
        })
      },
    }
  )
  const rebuildRelatedCardsMutation = useMutation(
    () => statusAPI.rebuildLojsRelatedCards(),
    {
      onSuccess: (result) => {
        setRelatedRebuildResult({
          ok: true,
          at: new Date().toISOString(),
          payload: result,
        })
        queryClient.invalidateQueries('systemStatus')
      },
      onError: (error) => {
        setRelatedRebuildResult({
          ok: false,
          at: new Date().toISOString(),
          message: error?.response?.data?.detail || error?.message || 'Не удалось перестроить похожие открытки',
        })
      },
    }
  )
  const waveModeMutation = useMutation(
    (mode) => statusAPI.setWebmasterWaveMode(mode),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('systemStatus')
      },
    }
  )
  const relatedWaveModeMutation = useMutation(
    (mode) => statusAPI.setRelatedWaveMode(mode),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('systemStatus')
      },
    }
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    )
  }

  const workers = data?.workers?.details || []
  const queue = data?.queue || {}
  const queueGroups = summarizeQueue(queue.pending)
  const images = data?.images || {}
  const sources = data?.sources || []
  const scheduler = data?.scheduler || {}
  const staleWorkers = data?.workers?.stale_details || []
  const services = data?.services || {}
  const lojsSeo = data?.lojs_seo || {}
  const webmasterAudit = data?.scheduler?.webmaster_audit || {}
  const webmasterAuditHistory = data?.scheduler?.webmaster_audit_history || {}
  const webmasterProfile = webmasterAudit?.profile || {}
  const currentWaveMode = webmasterProfile?.mode || 'base'
  const lojsPublished = lojsSeo?.published_images || {}
  const importedSeo = lojsSeo?.aggregator_external || {}
  const legacySeo = lojsSeo?.legacy_local || {}
  const recentSeoWindow = lojsSeo?.recent_window || {}
  const recentLegacyWindow = Array.isArray(recentSeoWindow?.by_origin)
    ? (recentSeoWindow.by_origin.find((row) => row.content_origin === 'legacy_local') || {})
    : {}
  const lojsMaintenance = data?.lojs_maintenance || {}
  const lojsRelatedMaintenance = data?.lojs_related_maintenance || {}
  const relayJobs = lojsSeo?.relay_jobs || {}
  const duplicateSnapshot = lojsSeo?.duplicate_snapshot || {}
  const persistedRebuildResult = lojsMaintenance?.result || null
  const persistedRelatedRebuildResult = lojsRelatedMaintenance?.result || lojsMaintenance?.actions?.rebuild_related_cards?.result || null
  const effectiveRebuildResult = rebuildResult || (
    lojsMaintenance?.available && persistedRebuildResult
      ? {
          ok: Boolean(persistedRebuildResult.success),
          at: lojsMaintenance.last_run_at,
          payload: persistedRebuildResult,
        }
      : null
  )
  const effectiveRelatedRebuildResult = relatedRebuildResult || (
    lojsRelatedMaintenance?.available && persistedRelatedRebuildResult
      ? {
          ok: Boolean(persistedRelatedRebuildResult.success),
          at: lojsRelatedMaintenance.last_run_at || lojsMaintenance?.actions?.rebuild_related_cards?.last_run_at,
          payload: persistedRelatedRebuildResult,
        }
      : null
  )
  const rebuildPayload = effectiveRebuildResult?.payload || {}
  const relatedRebuildPayload = effectiveRelatedRebuildResult?.payload || {}
  const beforeTailPages = Array.isArray(rebuildPayload.beforeTail) ? rebuildPayload.beforeTail : []
  const afterTailPages = Array.isArray(rebuildPayload.afterTail) ? rebuildPayload.afterTail : []
  const rawTailPage = afterTailPages[0] || null
  const lastVisibleFeedPage = afterTailPages.find((page) => Number(page?.count || 0) >= 6) || afterTailPages[0] || null
  const visiblePageNumber = lastVisibleFeedPage?.pageNumber || null
  const rawTailPageNumber = rawTailPage?.pageNumber || null
  const rawTailCount = Number(rawTailPage?.count || 0)
  const lojsHomeUrl = 'https://legkie-otkrytki.ru/'
  const lojsVisibleTailUrl = visiblePageNumber ? `https://legkie-otkrytki.ru/page-${visiblePageNumber}` : null
  const relatedMissing = Number(lojsRelatedMaintenance?.missing || 0)
  const relatedSchedulerStatus = scheduler?.lojs_related_maintenance || {}
  const currentRelatedWaveMode = lojsRelatedMaintenance?.mode || relatedSchedulerStatus?.mode || 'on'
  const relatedWaveLimit = Number(lojsRelatedMaintenance?.wave_limit || relatedRebuildPayload?.limit || relatedSchedulerStatus?.limit || 0)
  const relatedIntervalMinutes = Number(lojsRelatedMaintenance?.interval_minutes || 0)
  const relatedLastTrigger = lojsRelatedMaintenance?.trigger || lojsMaintenance?.actions?.rebuild_related_cards?.trigger || '—'
  const serviceEntries = Object.entries(SERVICE_META).map(([key, meta]) => ({
    key,
    ...meta,
    pending: services?.[key]?.pending || 0,
    processing: services?.[key]?.processing || 0,
  }))

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <ServerCog className="w-7 h-7 text-cyan-300" />
            Система
          </h1>
          <p className="text-white/60 mt-1">
            Статус воркеров, очередей, расписания источников и пайплайна
          </p>
        </div>
        <div className="glass px-4 py-2 text-sm">
          <div className="opacity-50">Обновлено</div>
          <div className="font-medium">
            {formatDateTime(data?.timestamp)}
            {isFetching ? ' • синхронизация...' : ''}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <SummaryCard
          icon={Activity}
          label="Воркеры"
          value={data?.workers?.active || 0}
          subtitle="активная обработка"
          colorClass="text-cyan-300"
        />
        <SummaryCard
          icon={Clock3}
          label="Очередь ожидает"
          value={queue.pending_total || 0}
          subtitle="все типы задач"
          colorClass="text-yellow-300"
        />
        <SummaryCard
          icon={Loader}
          label="В обработке"
          value={queue.processing_total || 0}
          subtitle="запущенные задачи"
          colorClass="text-cyan-300"
        />
        <SummaryCard
          icon={AlertCircle}
          label="Зависшие задачи"
          value={queue.stale_processing_total || 0}
          subtitle={queue.stale_threshold_minutes ? `>${queue.stale_threshold_minutes} мин` : 'зависшие задачи'}
          colorClass={(queue.stale_processing_total || 0) > 0 ? 'text-red-300' : 'text-green-300'}
        />
        <SummaryCard
          icon={CheckCircle2}
          label="Завершено сегодня"
          value={queue.completed_today || 0}
          subtitle="выполненные задачи"
          colorClass="text-green-300"
        />
        <SummaryCard
          icon={AlertCircle}
          label="Ошибок сегодня"
          value={queue.failed_today || 0}
          subtitle="нужна проверка"
          colorClass="text-red-300"
        />
        <SummaryCard
          icon={Upload}
          label="Опубликовано"
          value={images.published || 0}
          subtitle="уже в проде"
          colorClass="text-blue-300"
        />
        <SummaryCard
          icon={Image}
          label="Нужна проверка"
          value={images.needs_review || 0}
          subtitle="модерация изображений"
          colorClass="text-purple-300"
        />
      </div>

      <div className="glass p-5">
        <SectionTitle
          icon={Sparkles}
          title="SEO-пайплайн LOJS"
          subtitle="Публикация на сайт и прогресс AI SEO для legkie-otkrytki.ru"
        />

        {!lojsSeo?.available ? (
          <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-4 text-sm text-red-200">
            {lojsSeo?.error || 'Статус LOJS SEO недоступен'}
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              <SummaryCard
                icon={Link2}
                label="На сайте"
                value={lojsPublished.published_total || 0}
                subtitle="все опубликованные страницы"
                colorClass="text-blue-300"
              />
              <SummaryCard
                icon={Globe}
                label="Импортировано"
                value={lojsPublished.aggregator_external || 0}
                subtitle="из агрегатора"
                colorClass="text-cyan-300"
              />
              <SummaryCard
                icon={Sparkles}
                label="SEO готово"
                value={importedSeo.done || 0}
                subtitle="aggregator_external"
                colorClass="text-green-300"
              />
              <SummaryCard
                icon={Clock3}
                label="SEO в очереди"
                value={importedSeo.pending || 0}
                subtitle="ожидает AI"
                colorClass="text-yellow-300"
              />
              <SummaryCard
                icon={AlertCircle}
                label="SEO с ошибкой"
                value={importedSeo.failed || 0}
                subtitle="нужен повтор"
                colorClass={(importedSeo.failed || 0) > 0 ? 'text-red-300' : 'text-green-300'}
              />
              <SummaryCard
                icon={FileText}
                label="Legacy готово"
                value={legacySeo.done || 0}
                subtitle="старые страницы улучшены"
                colorClass="text-violet-300"
              />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
              <div className="rounded-xl bg-white/5 p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="text-sm opacity-60">Обслуживание главной</div>
                    <div className="text-xs opacity-50 mt-1">
                      Перестраивает mixed-ленту и очищает файловый кэш сайта
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => rebuildHomeFeedMutation.mutate()}
                    disabled={rebuildHomeFeedMutation.isLoading}
                    className="px-3 py-2 rounded-lg bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                  >
                    <Wrench size={16} />
                    {rebuildHomeFeedMutation.isLoading ? 'Перестраиваю...' : 'Перестроить главную'}
                  </button>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="opacity-70">Хвост до запуска</span>
                    <span className="font-medium">
                      {beforeTailPages[0]?.pageNumber || '—'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="opacity-70">Хвост после запуска</span>
                    <span className="font-medium text-cyan-300 flex items-center gap-2">
                      <span>{rawTailPageNumber || '—'}</span>
                      {rawTailPageNumber ? (
                        <span className="px-2 py-0.5 rounded bg-cyan-500/15 text-[11px] text-cyan-200">
                          {rawTailCount}/6
                        </span>
                      ) : null}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="opacity-70">Последняя полная</span>
                    <span className="font-medium text-green-300">
                      {visiblePageNumber || '—'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="opacity-70">Статус</span>
                    <span className={`font-medium ${effectiveRebuildResult?.ok === false ? 'text-red-300' : effectiveRebuildResult?.ok ? 'text-green-300' : 'text-white/60'}`}>
                      {effectiveRebuildResult?.ok === false ? 'ошибка' : effectiveRebuildResult?.ok ? 'выполнено' : 'не запускалось'}
                    </span>
                  </div>
                  <div className="text-xs opacity-50 pt-2">
                    {effectiveRebuildResult?.at ? `Последний запуск: ${formatDateTime(effectiveRebuildResult.at)}` : 'Ручной запуск ещё не выполнялся.'}
                  </div>
                  {effectiveRebuildResult?.ok === false ? (
                    <div className="text-xs text-red-200/80">{effectiveRebuildResult.message}</div>
                  ) : null}
                  {effectiveRebuildResult?.ok && rebuildPayload?.summary ? (
                    <div className="text-xs opacity-60">
                      Страниц в mixed-ленте: {rebuildPayload.summary.totalPages || 0}, карточек: {rebuildPayload.summary.totalRows || 0}
                    </div>
                  ) : null}
                  {rawTailPageNumber && visiblePageNumber && rawTailPageNumber !== visiblePageNumber ? (
                    <div className="text-xs opacity-50">
                      Технический хвост {rawTailPageNumber} пока неполный, поэтому для просмотра лучше открывать страницу {visiblePageNumber}.
                    </div>
                  ) : null}
                  <div className="flex items-center gap-2 pt-2 text-xs">
                    <a
                      href={lojsHomeUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="px-2 py-1 rounded bg-white/10 hover:bg-white/15 text-white/80"
                    >
                      Открыть главную
                    </a>
                    {lojsVisibleTailUrl ? (
                      <a
                        href={lojsVisibleTailUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="px-2 py-1 rounded bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-200"
                      >
                        Открыть последнюю полную {visiblePageNumber}
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-white/5 p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="text-sm opacity-60">Похожие открытки на карточках</div>
                    <div className="text-xs opacity-50 mt-1">
                      Автоволна дополняет пустые блоки похожих карточек на сайте без ручного запуска
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => rebuildRelatedCardsMutation.mutate()}
                    disabled={rebuildRelatedCardsMutation.isLoading}
                    className="px-3 py-2 rounded-lg bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                  >
                    <Wrench size={16} />
                    {rebuildRelatedCardsMutation.isLoading ? 'Запускаю...' : 'Запустить сейчас'}
                  </button>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                    {Object.entries(RELATED_WAVE_MODE_META).map(([mode, meta]) => {
                      const isActive = currentRelatedWaveMode === mode
                      const isLoadingMode = relatedWaveModeMutation.isLoading && relatedWaveModeMutation.variables === mode
                      return (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => relatedWaveModeMutation.mutate(mode)}
                          disabled={relatedWaveModeMutation.isLoading}
                          className={`rounded-lg px-3 py-3 text-left transition-all border ${
                            isActive
                              ? meta.activeClassName + ' border-transparent'
                              : 'bg-white/5 border-white/10 hover:bg-white/10 text-white'
                          } ${relatedWaveModeMutation.isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                          <div className="text-sm font-semibold">{isLoadingMode ? 'Сохраняю...' : meta.label}</div>
                          <div className={`text-xs mt-1 ${isActive ? 'opacity-80' : 'opacity-50'}`}>{meta.description}</div>
                        </button>
                      )
                    })}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="opacity-70">Режим</span>
                    <span className="font-medium">
                      {RELATED_WAVE_MODE_META[currentRelatedWaveMode]?.label || currentRelatedWaveMode}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="opacity-70">Статус</span>
                    <span className={currentRelatedWaveMode === 'off' ? 'font-medium text-red-300' : 'font-medium text-green-300'}>
                      {currentRelatedWaveMode === 'off' ? 'выключено вручную' : 'включено'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="opacity-70">Осталось пустых</span>
                    <span className={`font-medium ${relatedMissing > 0 ? 'text-yellow-300' : 'text-green-300'}`}>
                      {relatedMissing}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="opacity-70">Интервал автоволны</span>
                    <span className="font-medium">{relatedIntervalMinutes ? `${relatedIntervalMinutes} мин` : '—'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="opacity-70">Лимит за волну</span>
                    <span className="font-medium">{relatedWaveLimit || '—'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="opacity-70">Последний запуск</span>
                    <span className="font-medium">{formatDateTime(effectiveRelatedRebuildResult?.at)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="opacity-70">Источник запуска</span>
                    <span className={`font-medium ${relatedLastTrigger === 'auto' ? 'text-cyan-300' : 'text-white'}`}>
                      {relatedLastTrigger === 'auto' ? 'авто' : relatedLastTrigger === 'manual' ? 'вручную' : '—'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="opacity-70">Просмотрено / обновлено</span>
                    <span className="font-medium text-emerald-300">
                      {relatedRebuildPayload?.scanned || 0} / {relatedRebuildPayload?.updated || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="opacity-70">Пропущено</span>
                    <span className="font-medium">{relatedRebuildPayload?.skipped || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="opacity-70">Статус</span>
                    <span className={`font-medium ${effectiveRelatedRebuildResult?.ok === false ? 'text-red-300' : effectiveRelatedRebuildResult?.ok ? 'text-green-300' : 'text-white/60'}`}>
                      {effectiveRelatedRebuildResult?.ok === false ? 'ошибка' : effectiveRelatedRebuildResult?.ok ? 'выполнено' : 'ожидание'}
                    </span>
                  </div>
                  {effectiveRelatedRebuildResult?.ok === false ? (
                    <div className="text-xs text-red-200/80">{effectiveRelatedRebuildResult.message}</div>
                  ) : null}
                  <div className="text-xs opacity-50 pt-2">
                    Планировщик сам запускает эту волну регулярно, если режим включен. Кнопка выше нужна для ручного пуска, а переключатель — для паузы.
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-white/5 p-4">
                <div className="text-sm opacity-60 mb-3">AI-задачи</div>
                <div className="space-y-2 text-sm">
                  {Object.keys(relayJobs).length ? (
                    Object.entries(relayJobs).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between">
                        <span className="opacity-70">{status}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))
                  ) : (
                    <div className="opacity-50">AI-задач не найдено.</div>
                  )}
                </div>
              </div>

              <div className="rounded-xl bg-white/5 p-4">
                <div className="text-sm opacity-60 mb-3">Срез дублей</div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="opacity-70">Проверено строк</span>
                    <span className="font-medium">{duplicateSnapshot.rows_checked || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="opacity-70">Подозрительных пар</span>
                    <span className={`font-medium ${(duplicateSnapshot.suspicious_pairs || 0) > 0 ? 'text-yellow-300' : 'text-green-300'}`}>
                      {duplicateSnapshot.suspicious_pairs || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="opacity-70">Целей на регенерацию</span>
                    <span className={`font-medium ${(duplicateSnapshot.regeneration_targets || 0) > 0 ? 'text-yellow-300' : 'text-green-300'}`}>
                      {duplicateSnapshot.regeneration_targets || 0}
                    </span>
                  </div>
                  <div className="text-xs opacity-50 pt-2">
                    Обновлено: {formatRelativeStatus(lojsSeo.generated_at)}
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-white/5 p-4">
                <div className="text-sm opacity-60 mb-3">Статус по источникам</div>
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="font-medium text-cyan-300 mb-1">Aggregator external</div>
                    <div className="flex items-center justify-between opacity-70">
                      <span>готово / в очереди / ошибка</span>
                      <span>{importedSeo.done || 0} / {importedSeo.pending || 0} / {importedSeo.failed || 0}</span>
                    </div>
                  </div>
                  <div>
                    <div className="font-medium text-violet-300 mb-1">Legacy local</div>
                    <div className="flex items-center justify-between opacity-70">
                      <span>готово / в очереди / ошибка / нет</span>
                      <span>{legacySeo.done || 0} / {legacySeo.pending || 0} / {legacySeo.failed || 0} / {legacySeo.none || 0}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-white/5 p-4">
                <div className="text-sm opacity-60 mb-3">Автоволны SEO cleanup</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
                  {Object.entries(WEBMASTER_MODE_META).map(([mode, meta]) => {
                    const isActive = currentWaveMode === mode
                    const isLoadingMode = waveModeMutation.isLoading && waveModeMutation.variables === mode
                    return (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => waveModeMutation.mutate(mode)}
                        disabled={waveModeMutation.isLoading}
                        className={`rounded-lg px-3 py-3 text-left transition-all border ${
                          isActive
                            ? meta.activeClassName + ' border-transparent'
                            : 'bg-white/5 border-white/10 hover:bg-white/10 text-white'
                        } ${waveModeMutation.isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        <div className="text-sm font-semibold">{isLoadingMode ? 'Сохраняю...' : meta.label}</div>
                        <div className={`text-xs mt-1 ${isActive ? 'opacity-80' : 'opacity-50'}`}>{meta.description}</div>
                      </button>
                    )
                  })}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between opacity-70">
                    <span>Режим</span>
                    <span className="font-medium">
                      {WEBMASTER_MODE_META[currentWaveMode]?.label || currentWaveMode}
                    </span>
                  </div>
                  <div className="flex items-center justify-between opacity-70">
                    <span>Статус</span>
                    <span className={
                      currentWaveMode === 'off'
                        ? 'text-red-300'
                        : (webmasterAudit?.enabled ? 'text-green-300' : 'text-red-300')
                    }>
                      {currentWaveMode === 'off'
                        ? 'выключено вручную'
                        : (webmasterAudit?.enabled ? 'включено' : 'выключено')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between opacity-70">
                    <span>Активный профиль</span>
                    <span className={
                      webmasterProfile?.active_profile === 'off'
                        ? 'text-red-300'
                        : webmasterProfile?.active_profile === 'fast'
                          ? 'text-cyan-300'
                          : 'text-white'
                    }>
                      {webmasterProfile?.active_profile === 'off'
                        ? 'выключен'
                        : webmasterProfile?.active_profile === 'fast'
                          ? 'ускоренный'
                          : 'базовый'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between opacity-70">
                    <span>Текущий интервал</span>
                    <span>{webmasterAudit?.interval_minutes || '—'} мин</span>
                  </div>
                  <div className="flex items-center justify-between opacity-70">
                    <span>Базовый / быстрый</span>
                    <span>{webmasterProfile?.base_interval_minutes || '—'} / {webmasterProfile?.fast_interval_minutes || '—'} мин</span>
                  </div>
                  <div className="flex items-center justify-between opacity-70">
                    <span>Ночное окно</span>
                    <span>
                      {webmasterProfile?.night_mode_enabled
                        ? `${webmasterProfile?.night_start_hour ?? '—'}:00-${webmasterProfile?.night_end_hour ?? '—'}:00`
                        : 'выкл'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between opacity-70">
                    <span>Ночной режим сейчас</span>
                    <span className={webmasterProfile?.night_window_active ? 'text-cyan-300' : 'text-white/60'}>
                      {webmasterProfile?.night_window_active ? 'активен' : 'нет'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between opacity-70">
                    <span>Переключено</span>
                    <span>{formatRelativeStatus(webmasterProfile?.mode_updated_at)}</span>
                  </div>
                  <div className="flex items-center justify-between opacity-70">
                    <span>Размер волны</span>
                    <span>{webmasterAudit?.wave_limit || '—'} стр.</span>
                  </div>
                  <div className="flex items-center justify-between opacity-70">
                    <span>Волн за {webmasterAuditHistory?.window_hours || 12}ч</span>
                    <span>{webmasterAuditHistory?.waves || 0}</span>
                  </div>
                  <div className="flex items-center justify-between opacity-70">
                    <span>Поставлено в очередь</span>
                    <span className="text-cyan-300">{webmasterAuditHistory?.enqueued || 0}</span>
                  </div>
                  <div className="flex items-center justify-between opacity-70">
                    <span>Последняя волна</span>
                    <span>{formatRelativeStatus(webmasterAuditHistory?.last_wave_at)}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-white/5 p-4">
                <div className="text-sm opacity-60 mb-3">SEO за {recentSeoWindow?.window_hours || 12} часов</div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between opacity-70">
                    <span>Готово всего</span>
                    <span className="text-green-300">{recentSeoWindow?.done_total || 0}</span>
                  </div>
                  <div className="flex items-center justify-between opacity-70">
                    <span>Ошибок всего</span>
                    <span className={(recentSeoWindow?.failed_total || 0) > 0 ? 'text-red-300' : 'text-green-300'}>
                      {recentSeoWindow?.failed_total || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between opacity-70">
                    <span>Legacy готово</span>
                    <span className="text-violet-300">{recentLegacyWindow?.done_total || 0}</span>
                  </div>
                  <div className="flex items-center justify-between opacity-70">
                    <span>Legacy ошибок</span>
                    <span className={(recentLegacyWindow?.failed_total || 0) > 0 ? 'text-red-300' : 'text-green-300'}>
                      {recentLegacyWindow?.failed_total || 0}
                    </span>
                  </div>
                  <div className="text-xs opacity-50 pt-2">
                    Показывает реальную активность пайплайна за последнее окно, чтобы утром было видно, что волны ночью действительно шли.
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className="rounded-xl bg-white/5 p-4">
                <div className="text-sm opacity-60 mb-3">Последние завершённые SEO</div>
                {!lojsSeo?.recent_completed_jobs?.length ? (
                  <div className="text-sm opacity-50">Недавних завершённых задач нет.</div>
                ) : (
                  <div className="space-y-3">
                    {lojsSeo.recent_completed_jobs.map(job => (
                      <div key={`done-${job.id}`} className="rounded-lg bg-white/5 p-3">
                        <div className="flex items-center justify-between gap-3 text-sm">
                          <div className="font-medium">#{job.id} · image {job.entity_id}</div>
                          <div className="text-green-300">{job.content_origin || '—'}</div>
                        </div>
                        <div className="text-sm opacity-70 mt-1">{job.alt || '—'}</div>
                        <div className="text-xs opacity-50 mt-2">
                          {job.subcategory_name || '—'} · {formatDateTime(job.completed_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-xl bg-white/5 p-4">
                <div className="text-sm opacity-60 mb-3">Последние SEO с ошибкой</div>
                {!lojsSeo?.recent_failed_jobs?.length ? (
                  <div className="text-sm opacity-50">Задач с ошибкой нет.</div>
                ) : (
                  <div className="space-y-3">
                    {lojsSeo.recent_failed_jobs.map(job => (
                      <div key={`failed-${job.id}`} className="rounded-lg bg-white/5 p-3">
                        <div className="flex items-center justify-between gap-3 text-sm">
                          <div className="font-medium">#{job.id} · image {job.entity_id}</div>
                          <div className="text-red-300">{job.content_origin || '—'}</div>
                        </div>
                        <div className="text-sm opacity-70 mt-1">{job.alt || '—'}</div>
                        <div className="text-xs text-red-200/80 mt-2">{job.error_text || 'Неизвестная ошибка'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="glass p-5">
          <SectionTitle
            icon={Loader}
            title="Активные воркеры"
            subtitle="Задачи, которые обрабатываются прямо сейчас"
          />

          {!workers.length ? (
            <div className="text-sm opacity-50">Сейчас активных задач нет.</div>
          ) : (
            <div className="space-y-3">
              {workers.map(worker => (
                <div key={`${worker.worker_id}-${worker.task_id}`} className="rounded-xl bg-white/5 p-4">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="font-medium">{worker.worker_id}</div>
                    <div className="px-2 py-1 rounded bg-blue-500/15 text-blue-300 text-xs">
                      {worker.task_type}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                    <div>
                      <div className="opacity-50">Задача</div>
                      <div>#{worker.task_id}</div>
                    </div>
                    <div>
                      <div className="opacity-50">Старт</div>
                      <div>{formatDateTime(worker.started_at)}</div>
                    </div>
                    <div>
                      <div className="opacity-50">Работает</div>
                      <div>{formatSeconds(worker.running_seconds)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="glass p-5">
          <SectionTitle
            icon={Radar}
            title="Потоки очереди"
            subtitle="Нагрузка по ожидающим и обрабатываемым задачам"
          />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <SummaryCard icon={Globe} label="Парсинг" value={queueGroups.parse} colorClass="text-cyan-300" />
            <SummaryCard icon={Globe} label="Детали" value={queueGroups.parseDetails} colorClass="text-sky-300" />
            <SummaryCard icon={Download} label="Скачивание" value={queueGroups.download} colorClass="text-yellow-300" />
            <SummaryCard icon={Upload} label="Публикация" value={queueGroups.publish} colorClass="text-green-300" />
            <SummaryCard icon={HardDrive} label="Бэкфилл" value={queueGroups.backfill} colorClass="text-orange-300" />
            <SummaryCard icon={AlertCircle} label="Другое" value={queueGroups.other} colorClass="text-white" />
            <SummaryCard icon={Loader} label="В обработке" value={queue.processing_total || 0} colorClass="text-cyan-300" />
            <SummaryCard icon={ServerCog} label="Планировщик" value={scheduler.running ? 'ON' : 'OFF'} subtitle={scheduler.interval_seconds ? `интервал ${scheduler.interval_seconds}с` : 'интервал не задан'} colorClass={scheduler.running ? 'text-green-300' : 'text-red-300'} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl bg-white/5 p-4">
              <div className="text-sm opacity-60 mb-3">Ожидают по точному типу задачи</div>
              {!Object.keys(queue.pending || {}).length ? (
                <div className="text-sm opacity-50">В очереди нет ожидающих задач.</div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(queue.pending).map(([taskType, count]) => (
                    <div key={taskType} className="flex items-center justify-between text-sm">
                      <span className="opacity-70">{taskType}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl bg-white/5 p-4">
              <div className="text-sm opacity-60 mb-3">В обработке по точному типу задачи</div>
              {!Object.keys(queue.processing || {}).length ? (
                <div className="text-sm opacity-50">Сейчас нет задач в обработке.</div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(queue.processing).map(([taskType, count]) => (
                    <div key={taskType} className="flex items-center justify-between text-sm">
                      <span className="opacity-70">{taskType}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {(queue.stale_processing_total || 0) > 0 ? (
        <div className="glass p-5 border border-red-500/20">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <SectionTitle
                icon={AlertCircle}
                title="Зависшие задачи"
                subtitle={`Задачи старше ${queue.stale_threshold_minutes || 30} минут всё ещё помечены как обрабатываемые`}
              />
            </div>
            <button
              type="button"
              onClick={() => recoverStaleMutation.mutate()}
              disabled={recoverStaleMutation.isLoading}
              className="px-3 py-2 rounded-lg bg-red-500/15 text-red-200 hover:bg-red-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Wrench size={16} />
              {recoverStaleMutation.isLoading ? 'Восстанавливаю...' : 'Вернуть в очередь'}
            </button>
          </div>

          <div className="space-y-3">
            {staleWorkers.map(worker => (
              <div key={`stale-${worker.task_id}`} className="rounded-xl bg-white/5 p-4">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="font-medium">#{worker.task_id} · {worker.task_type}</div>
                  <div className="px-2 py-1 rounded bg-red-500/15 text-red-300 text-xs">
                    зависла
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                  <div>
                    <div className="opacity-50">Воркер</div>
                    <div>{worker.worker_id}</div>
                  </div>
                  <div>
                    <div className="opacity-50">Старт</div>
                    <div>{formatDateTime(worker.started_at)}</div>
                  </div>
                  <div>
                    <div className="opacity-50">Работает</div>
                    <div>{formatSeconds(worker.running_seconds)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 xl:grid-cols-[320px,1fr] gap-6">
        <div className="glass p-5">
          <SectionTitle
            icon={ServerCog}
            title="Планировщик"
            subtitle="Глобальный heartbeat для оркестрации очереди"
          />
          <SchedulerState scheduler={scheduler} />
        </div>

        <div className="glass p-5">
          <SectionTitle
            icon={Activity}
            title="Сервисы"
            subtitle="Разбиение сервисов на ожидающие и обрабатываемые задачи"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {serviceEntries.map(service => {
              const Icon = service.icon
              return (
                <div key={service.key} className="rounded-xl bg-white/5 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon size={16} className={service.colorClass} />
                    <div className={`text-sm font-medium ${service.colorClass}`}>{service.label}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="opacity-50">Ожидают</div>
                      <div className="text-lg font-semibold">{service.pending}</div>
                    </div>
                    <div>
                      <div className="opacity-50">В обработке</div>
                      <div className="text-lg font-semibold">{service.processing}</div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="glass p-5">
          <SectionTitle
            icon={Image}
            title="Нагрузка пайплайна"
            subtitle="Текущее распределение состояний изображений"
          />

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <SummaryCard icon={Image} label="Распарсено" value={images.parsed || 0} colorClass="text-white" />
            <SummaryCard icon={AlertCircle} label="На проверке" value={images.needs_review || 0} colorClass="text-purple-300" />
            <SummaryCard icon={CheckCircle2} label="Одобрено" value={images.approved || 0} colorClass="text-green-300" />
            <SummaryCard icon={Download} label="Скачано" value={images.downloaded || 0} colorClass="text-yellow-300" />
            <SummaryCard icon={Upload} label="Опубликовано" value={images.published || 0} colorClass="text-blue-300" />
          </div>
        </div>

        <div className="glass p-5">
          <SectionTitle
            icon={Activity}
            title="Сегодня"
            subtitle="Свежая активность за текущий UTC-день"
          />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard icon={Image} label="Новых изображений" value={data?.today?.new_images || 0} colorClass="text-cyan-300" />
            <SummaryCard icon={Globe} label="Новых авторов" value={data?.today?.new_authors || 0} colorClass="text-violet-300" />
            <SummaryCard icon={CheckCircle2} label="Задач выполнено" value={data?.today?.tasks_completed || 0} colorClass="text-green-300" />
            <SummaryCard icon={AlertCircle} label="Ошибок задач" value={data?.today?.tasks_failed || 0} colorClass="text-red-300" />
          </div>
        </div>
      </div>

      <div className="glass p-5">
        <SectionTitle
          icon={Globe}
          title="Расписание источников"
          subtitle="Когда каждый активный источник был распарсен и когда придёт его следующая очередь"
        />

        {!sources.length ? (
          <div className="text-sm opacity-50">Активные источники не найдены.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-dark">
              <thead>
                <tr>
                  <th>Источник</th>
                  <th>Последний парсинг</th>
                  <th>Следующий парсинг</th>
                  <th>Интервал</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                {sources.map(source => {
                  const freshness = freshnessState(source.next_parse)
                  return (
                    <tr key={source.id}>
                      <td className="font-medium">{source.name}</td>
                      <td className="text-sm opacity-70">{formatDateTime(source.last_parsed)}</td>
                      <td className="text-sm opacity-70">{formatDateTime(source.next_parse)}</td>
                      <td className="text-sm opacity-70">{source.parse_interval_minutes} мин</td>
                      <td>
                        <span className={`px-2 py-1 rounded text-xs ${freshness.className}`}>
                          {freshness.label}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
